import { create } from 'zustand';
import { UPLOAD_MAX_BYTES, uploadTooLargeSentence } from './pro-builder-copy';
import { sendProBuilderDocument, type ProBuilderUploadResult } from './pro-builder-upload-request';

export type UploadRowState = 'queued' | 'uploading' | 'done' | 'failed';

export interface UploadRow {
  id: number;
  /** Held only until the row finishes (done or failed), so finished rows do not keep file contents for the tab session. */
  file: File | null;
  /** The file's original name (the row's `data-file`). */
  name: string;
  /** The app folder this row was enqueued for; an app switch mid-queue makes the server refuse it. */
  folder: string;
  state: UploadRowState;
  progress: number;
  /** `Saved`, `Saved as <name>`, or the refusal sentence; empty while waiting or sending. */
  detail: string;
}

/** New rows for a pick or a drop; a file over the limit is born failed and is never sent. */
export function makeUploadRows(files: readonly File[], folder: string, firstId: number): UploadRow[] {
  return files.map((file, i) => {
    const tooLarge = file.size > UPLOAD_MAX_BYTES;
    return {
      id: firstId + i,
      file: tooLarge ? null : file,
      name: file.name,
      folder,
      state: tooLarge ? 'failed' : 'queued',
      progress: 0,
      detail: tooLarge ? uploadTooLargeSentence(file.name) : '',
    };
  });
}

/** The row to send next: the first queued one, and only while nothing is uploading (one at a time, in order). */
export function nextRowToSend(rows: readonly UploadRow[]): UploadRow | null {
  if (rows.some((r) => r.state === 'uploading')) return null;
  return rows.find((r) => r.state === 'queued') ?? null;
}

export function patchRow(rows: readonly UploadRow[], id: number, patch: Partial<UploadRow>): UploadRow[] {
  return rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
}

export function savedDetail(result: Pick<ProBuilderUploadResult, 'name' | 'renamed'>): string {
  return result.renamed ? `Saved as ${result.name}` : 'Saved';
}

type Sender = typeof sendProBuilderDocument;
let sender: Sender = sendProBuilderDocument;
/** Test seam. */
export function setUploadSenderForTest(next: Sender | null): void {
  sender = next ?? sendProBuilderDocument;
}

interface ProBuilderUploadStore {
  open: boolean;
  rows: UploadRow[];
  nextId: number;
  openDialog: () => void;
  closeDialog: () => void;
  enqueue: (files: readonly File[], folder: string) => void;
}

export const useProBuilderUploadStore = create<ProBuilderUploadStore>((set, get) => {
  const pump = (): void => {
    const row = nextRowToSend(get().rows);
    if (!row || !row.file) return;
    set((s) => ({ rows: patchRow(s.rows, row.id, { state: 'uploading', progress: 0 }) }));
    void sender({
      file: row.file,
      folder: row.folder,
      onProgress: (progress) => set((s) => ({ rows: patchRow(s.rows, row.id, { progress }) })),
    })
      .then(
        (result) => set((s) => ({ rows: patchRow(s.rows, row.id, { state: 'done', progress: 100, file: null, detail: savedDetail(result) }) })),
        (err: unknown) =>
          set((s) => ({ rows: patchRow(s.rows, row.id, { state: 'failed', file: null, detail: err instanceof Error ? err.message : 'The upload failed. Try again.' }) })),
      )
      .then(pump);
  };
  return {
    open: false,
    rows: [],
    nextId: 1,
    openDialog: () => set({ open: true }),
    closeDialog: () => set({ open: false }),
    enqueue: (files, folder) => {
      if (files.length === 0) return;
      const made = makeUploadRows(files, folder, get().nextId);
      set((s) => ({ rows: [...s.rows, ...made], nextId: s.nextId + made.length }));
      pump();
    },
  };
});

/** The terminal view not showing (window hidden, or another view) closes the upload dialog, so it cannot pop open again on its own when the terminal returns. */
export function closeUploadDialogWhenHidden(terminalShown: boolean): void {
  if (!terminalShown) useProBuilderUploadStore.getState().closeDialog();
}
